import React, { useState, useRef } from "react";
import { Upload, AlertCircle, Link } from "lucide-react";
import { uploadFileToS3 } from "../../../utils/awsS3";
import "./KnowledgeBase.css";
import { useCompany } from "../../../context/CompanyContext";
import axiosInstance from "../../../utils/api";

const KnowledgeBase = () => {
  const { companyId, companyKnowledgeDocs } = useCompany();
  const [files, setFiles] = useState([]);
  const [urls, setUrls] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);
  const urlInputRef = useRef(null);

  const allowedTypes = [
    "application/pdf",
    "image/jpeg",
    "image/png",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];

  const validateFile = (file) => {
    if (!allowedTypes.includes(file.type)) {
      setError("Invalid file type. Please upload PDF, JPEG, PNG or DOC files.");
      return false;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("File size should not exceed 10MB.");
      return false;
    }
    return true;
  };

  const getFileIcon = (fileType) => {
    if (fileType.includes("pdf")) {
      return "/pdf.svg";
    } else if (fileType.includes("image")) {
      return "/img.svg";
    }
    return "/doc.svg";
  };

  const uploadFile = async (file) => {
    const tempId = Date.now();
    try {
      setUploading(true);
      setError("");

      setFiles((prev) => [
        ...prev,
        {
          id: tempId,
          name: file.name,
          type: file.type,
          status: "uploading",
          progress: 0,
        },
      ]);

      const url = await uploadFileToS3(
        file,
        (pct) => {
          setFiles((prev) =>
            prev.map((f) => (f.id === tempId ? { ...f, progress: pct } : f))
          );
        },
        "knowledge-base"
      );

      setFiles((prev) =>
        prev.map((f) =>
          f.id === tempId ? { ...f, status: "completed", url } : f
        )
      );

      if (companyId) {
        try {
          console.log("Updating knowledge base for company:", companyId);
          // Use companyKnowledgeDocs from context instead of making GET API call
          const currentKnowledgeBase = Array.isArray(companyKnowledgeDocs)
            ? companyKnowledgeDocs.map(doc => doc.url || doc)
            : [];

          const updatedKnowledgeBase = Array.from(
            new Set([...currentKnowledgeBase, url])
          );

          setUrls(updatedKnowledgeBase);

          // Still need to PATCH to update the company, but don't GET first
          // Get current additional_infos from context or use minimal payload
          const finalPayload = {
            company: {
              additional_infos: {
                knowledge_base: updatedKnowledgeBase,
              },
            },
          };

          await axiosInstance.patch(
            `/api/v1/companies/${companyId}`,
            finalPayload
          );
          console.log("Successfully updated knowledge base");
        } catch (err) {
          console.error(
            "Failed to update knowledge base:",
            err.response ? err.response.data : err.message
          );
        }
      } else {
        console.error("companyId is missing, cannot update knowledge base.");
      }
    } catch (err) {
      setError(`Upload failed: ${err.message || "Unknown error occurred"}`);
      setFiles((prev) => prev.filter((f) => f.id !== tempId));
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    for (const file of selectedFiles) {
      if (validateFile(file)) {
        await uploadFile(file);
      }
    }
    e.target.value = null;
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    for (const file of droppedFiles) {
      if (validateFile(file)) {
        await uploadFile(file);
      }
    }
  };

  const handleCloudService = (service) => {
    console.log(`Connecting to ${service}...`);
  };

  const handleUrlUpload = async () => {
    const url = urlInputRef.current.value;
    if (!url) {
      setError("Please enter a valid URL");
      return;
    }

    try {
      setUploading(true);

      const fileName = url.split("/").pop() || "downloaded-file";
      const uploadedFile = {
        id: Date.now(),
        name: fileName,
        type: "url",
        status: "uploading",
        progress: 0,
      };

      setFiles((prev) => [...prev, uploadedFile]);

      let progress = 0;
      const progressInterval = setInterval(() => {
        progress += 10;
        if (progress <= 100) {
          setFiles((prev) =>
            prev.map((f) => (f.id === uploadedFile.id ? { ...f, progress } : f))
          );
        } else {
          clearInterval(progressInterval);
          setFiles((prev) =>
            prev.map((f) =>
              f.id === uploadedFile.id ? { ...f, status: "completed" } : f
            )
          );
        }
      }, 500);

      urlInputRef.current.value = "";
    } catch (err) {
      setError("Failed to upload file from URL");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = (fileId) => {
    setFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  return (
    <div className="container py-4">
      {error && (
        <div
          className="alert alert-danger d-flex align-items-center"
          role="alert"
        >
          <AlertCircle size={18} className="me-2" />
          {error}
          <button
            type="button"
            className="btn-close ms-auto"
            onClick={() => setError("")}
          />
        </div>
      )}

      <div className="row g-4">
        <div className="col-md-6">
          <div
            className={`upload-box ${dragActive ? "drag-active" : ""}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="d-none"
              onChange={handleFileSelect}
              accept={allowedTypes.join(",")}
            />
            <Upload size={24} />
            <p>
              Drag and drop files, or{" "}
              <span className="browse-link">Browse</span>
            </p>
            <small className="text-muted">
              Supported formats: PDF, JPEG, PNG, DOC (Max 10MB)
            </small>
          </div>
        </div>

        <div className="col-md-6">
          <div className="cloud-upload-section">
            <h2 className="mb-3 text-center fw-bold">
              <span className="text-danger">Knowledge </span> Base
            </h2>
            <p className="text-center fw-medium">
              Upload essential business documents
            </p>
            <p className="text-muted text-center">
              Share documents like brand guidelines and pitch <br></br> decks,
              etc to help us tailor growth strategies for<br></br> your
              business.
            </p>
            <p className="text-center fw-medium ">Connect Your Cloud Files</p>
            <div className="cloud-services">
              <button
                className="cloud-btn google btn"
                onClick={() => handleCloudService("Google Drive")}
              >
                <img src="/drive.svg" alt="Google Drive" />
              </button>
              <button
                className="cloud-btn amazon btn"
                onClick={() => handleCloudService("Amazon")}
              >
                <img src="/s3.svg" alt="Amazon" />
              </button>
              <button
                className="cloud-btn onedrive btn"
                onClick={() => handleCloudService("OneDrive")}
              >
                <img src="/onedrive.svg" alt="OneDrive" />
              </button>
              <button
                className="cloud-btn dropbox btn"
                onClick={() => handleCloudService("Dropbox")}
              >
                <img src="/dropbox.svg" alt="Dropbox" />
              </button>
            </div>
            <div className="url-input mt-3">
              <div className="input-group">
                <span className="input-group-text">
                  <Link size={18} />
                </span>
                <input
                  ref={urlInputRef}
                  type="text"
                  className="form-control"
                  placeholder="Type or paste the URL"
                />
                <button
                  className="btn linkuploadbtn"
                  onClick={handleUrlUpload}
                  disabled={uploading}
                >
                  <Upload size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {files.length > 0 && (
        <div className="row mt-4">
          <div className="col-12">
            <div className="uploaded-files-header">
              <h6>Uploaded files</h6>
              <span className="manage-files">Manage your uploaded files</span>
            </div>
            <div className="uploaded-files">
              {files.map((file) => (
                <div
                  key={file.id}
                  className={`file-card ${
                    file.status === "uploading" ? "progress-card" : ""
                  }`}
                >
                  {file.status === "uploading" ? (
                    <>
                      <div className="progress-circle">
                        <i className="fas fa-sync-alt fa-spin"></i>
                      </div>
                      <div className="file-info">
                        <div className="file-name text-white">
                          Uploading...
                        </div>
                        <div className="progress-value">{file.progress}%</div>
                        <div className="progress">
                          <div
                            className="progress-bar"
                            style={{ width: `${file.progress}%` }}
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {file.status === "updating" && (
                        <span className="updating-label">Updating</span>
                      )}
                      <div className="file-icon">
                        <img src={getFileIcon(file.type)} alt={file.type} />
                      </div>
                      <div className="file-info">
                        <div className="file-name">{file.name}</div>
                        <div className="file-size">720KB</div>
                        <div className="file-tags">
                          {file.type.includes("pdf") && (
                            <span className="file-tag">Pitch Deck</span>
                          )}
                          {file.type.includes("image") && (
                            <button className="file-tag add-tag">
                              <i className="fas fa-plus"></i> Add tags
                            </button>
                          )}
                          {file.type.includes("doc") && (
                            <>
                              <span className="file-tag">Brand Guide</span>
                              <span className="file-tag">Pricing</span>
                            </>
                          )}
                        </div>
                      </div>
                      <button className="file-menu">
                        <i className="fas fa-ellipsis-v"></i>
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <hr></hr>

      <div className="row mt-4">
        <div className="col-12 d-flex justify-content-end">
          <button className="btn me-2">Back</button>
          <button
            className="btn btn-dark"
            disabled={uploading || files.length === 0}
            style={{
              cursor:
                uploading || files.length === 0 ? "not-allowed" : "pointer",
            }}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBase;
